<template>
  <!-- 监护统计：卡片分区布局（2026-10-24 由手风琴改版），禁止 el-container 包整页 -->
  <div class="app-container monitor-stat-page">
    <!-- ============ 一、数据录入：左主（人次）右辅（台数）双栏 ============ -->
    <div class="entry-row">
      <section class="ms-card entry-main">
        <header class="ms-card-hd">
          <span class="ms-card-title">每日监护人次上传</span>
          <span class="ms-card-sub">上传《信息日报表》图片 → OCR 识别 → 核对后入库</span>
        </header>
        <div class="ms-card-bd">
          <div class="upload-row">
            <el-upload
              ref="imgUpload"
              action="#"
              :auto-upload="false"
              :show-file-list="true"
              :limit="1"
              accept="image/*"
              :on-change="onImageChange"
              :on-remove="onImageRemove"
            >
              <el-button size="small" icon="el-icon-picture">选择日报图片</el-button>
            </el-upload>
            <el-button
              size="small"
              type="primary"
              icon="el-icon-search"
              :loading="previewing"
              :disabled="!imageFile"
              @click="handlePreview"
            >识别预览</el-button>
          </div>

          <!-- 校验条：行合计 vs 报表合计 -->
          <el-alert
            v-if="previewDone"
            :type="preview.matched ? 'success' : 'warning'"
            :closable="false"
            show-icon
            class="match-alert"
            :title="preview.matched
              ? `与报表合计一致（行合计 ${preview.row_total} = 报表合计 ${preview.report_total}）`
              : `不一致，请人工核对修正（行合计 ${preview.row_total} / 报表合计 ${preview.report_total}）`"
          />

          <!-- 识别结果：整表完整铺开，不截断、不内滚，页面自然滚动 -->
          <template v-if="previewDone">
            <div class="table-toolbar">
              <el-date-picker
                v-model="statDate"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="统计日期"
                size="small"
                style="width: 160px"
              />
              <el-button size="small" icon="el-icon-plus" @click="addRow">添加科室</el-button>
              <el-button
                size="small"
                type="success"
                icon="el-icon-upload"
                :loading="confirming"
                @click="handleConfirm"
              >确认入库</el-button>
            </div>

            <el-table :data="preview.rows" border size="small" class="edit-table">
              <el-table-column type="index" label="序号" width="55" align="center" />
              <el-table-column label="科室" min-width="160">
                <template slot-scope="scope">
                  <el-input v-model="scope.row.department" size="small" placeholder="科室名" />
                </template>
              </el-table-column>
              <el-table-column label="监护人次" width="160" align="center">
                <template slot-scope="scope">
                  <el-input-number
                    v-model="scope.row.monitor_person_times"
                    :min="0"
                    size="small"
                    controls-position="right"
                    style="width: 130px"
                  />
                </template>
              </el-table-column>
              <el-table-column label="操作" width="90" align="center">
                <template slot-scope="scope">
                  <el-button type="text" size="small" class="danger-link" @click="removeRow(scope.$index)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </template>
        </div>
      </section>

      <!-- 右辅：监护仪台数清单（设备有增减时才用） -->
      <section class="ms-card entry-side">
        <header class="ms-card-hd">
          <span class="ms-card-title">监护仪台数清单</span>
          <span class="ms-card-sub">设备有增减时才需上传</span>
        </header>
        <div class="ms-card-bd">
          <div class="upload-row">
            <el-upload
              ref="excelUpload"
              action="#"
              :auto-upload="false"
              :show-file-list="true"
              :limit="1"
              accept=".xlsx,.xls"
              :on-change="onExcelChange"
              :on-remove="onExcelRemove"
            >
              <el-button size="small" icon="el-icon-document">选择 Excel 清单</el-button>
            </el-upload>
            <el-date-picker
              v-model="excelDate"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="落库日期"
              size="small"
              style="width: 150px"
            />
            <el-button
              size="small"
              type="primary"
              icon="el-icon-upload2"
              :loading="uploadingExcel"
              :disabled="!excelFile"
              @click="handleExcelUpload"
            >上传清单</el-button>
          </div>

          <el-alert
            v-if="excelResult"
            type="success"
            :closable="false"
            show-icon
            class="match-alert"
            :title="`上传成功：合计监护仪 ${excelResult.total_devices} 台，覆盖 ${excelResult.dept_count} 个科室`"
          />
          <p class="side-tip">
            Excel 清单只用于统计「各科室台数」，与每日人次互不影响；<br>
            没有变动就不用重复上传。
          </p>
        </div>
      </section>
    </div>

    <!-- ============ 二、当前统计 ============ -->
    <section class="ms-card">
      <header class="ms-card-hd">
        <span class="ms-card-title">当前统计</span>
        <span class="ms-card-sub">分科室监护人次与监护仪台数联合明细</span>
        <span class="ms-card-tools">
          <span v-if="dashboard.person_date" class="dash-date">统计日期：{{ dashboard.person_date }}</span>
          <el-button size="mini" icon="el-icon-refresh" :loading="loadingDash" @click="fetchDashboard">刷新</el-button>
        </span>
      </header>
      <div class="ms-card-bd">
        <!-- KPI 三连：比原来一行文字合计更直观 -->
        <div class="kpi-row" v-if="dashboard.rows.length">
          <div class="kpi">
            <span class="kpi-val">{{ dashboard.person_total }}</span><span class="kpi-unit">人次</span>
            <span class="kpi-label">监护人次合计</span>
          </div>
          <div
            class="kpi"
            :title="dashboard.device_total_fixed ? '一期按全院固定 130 台显示（设备台账未录全），后期按设备台账统计' : ''"
          >
            <span class="kpi-val">{{ dashboard.device_total }}</span><span class="kpi-unit">台</span>
            <span class="kpi-label">
              监护仪台数
              <i v-if="dashboard.device_total_fixed" class="fixed-flag">一期固定值</i>
            </span>
          </div>
          <div class="kpi">
            <span class="kpi-val">{{ dashboard.rows.length }}</span><span class="kpi-unit">个</span>
            <span class="kpi-label">覆盖科室</span>
          </div>
        </div>

        <!-- 当前统计表：完整铺开，不截断（「合计」列已去掉：人次+台数相加无业务含义） -->
        <el-table :data="dashboard.rows" border size="small" class="dash-table">
          <el-table-column type="index" label="序号" width="55" align="center" />
          <el-table-column prop="department" label="科室" min-width="160" />
          <el-table-column prop="monitor_person_times" label="监护人次" width="140" align="center" />
          <el-table-column label="监护仪台数" width="140" align="center">
            <template slot-scope="scope">{{ scope.row.monitor_devices == null ? '-' : scope.row.monitor_devices }}</template>
          </el-table-column>
        </el-table>

        <div v-if="!dashboard.rows.length" class="mini-empty">暂无数据，请先上传当日统计图片</div>
      </div>
    </section>

    <!-- ============ 三、历史查询 ============ -->
    <section class="ms-card">
      <header class="ms-card-hd">
        <span class="ms-card-title">历史查询</span>
        <span class="ms-card-sub">按日期区间 / 科室查看历史数据，可导出 Excel、可核对日报原图</span>
      </header>
      <div class="ms-card-bd">
        <!-- 独立组件：汇总 / 明细 / 日报原图 三个页签 -->
        <MonitorStatHistory ref="history" />
      </div>
    </section>
  </div>
</template>

<script>
import monitorStatApi from '../api/monitorStat'
import MonitorStatHistory from './MonitorStatHistory.vue'
import { Message } from 'element-ui'

export default {
  name: 'MonitorStatUpload',
  components: { MonitorStatHistory },
  data() {
    return {
      // 区块1
      imageFile: null,
      previewing: false,
      confirming: false,
      previewDone: false,
      preview: { rows: [], row_total: 0, report_total: null, matched: false },
      statDate: this.todayStr(),
      previewFileId: null, // 原图留档 id：确认入库时回填真实统计日期
      // 区块2
      excelFile: null,
      excelDate: this.todayStr(), // 台数落库日期（不再是后端写死 today）
      uploadingExcel: false,
      excelResult: null,
      // 区块3
      loadingDash: false,
      dashboard: {
        person_date: null,
        person_total: 0,
        device_total: 0,
        // 台数是否为一期固定值（后端标记，用来显示「一期固定值」小标）
        device_total_fixed: false,
        rows: [],
      },
    }
  },
  mounted() {
    this.fetchDashboard()
  },
  methods: {
    todayStr() {
      const d = new Date()
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    // ---- 区块1：图片选择/识别/入库 ----
    onImageChange(file) {
      const raw = file.raw || file
      if (!raw) return
      // 仅接受图片
      if (raw.type && raw.type.indexOf('image/') !== 0) {
        Message.warning('请选择图片文件')
        this.$refs.imgUpload.clearFiles()
        this.imageFile = null
        return
      }
      this.imageFile = raw
      this.previewDone = false
    },
    onImageRemove() {
      this.imageFile = null
      this.previewDone = false
    },
    async handlePreview() {
      if (!this.imageFile) return
      this.previewing = true
      try {
        const fd = new FormData()
        fd.append('file', this.imageFile)
        const res = await monitorStatApi.previewImage(fd)
        const d = res.data || {}
        this.preview = {
          rows: Array.isArray(d.rows) ? d.rows : [],
          row_total: d.row_total || 0,
          report_total: d.report_total,
          matched: !!d.matched,
        }
        // 后端在预览阶段就把原图留档了，记下 id，确认入库时回填真实日期
        this.previewFileId = d.file_id || null
        this.previewDone = true
        if (!this.statDate) this.statDate = this.todayStr()
      } catch (e) {
        // 透出后端真实错误（detail / non_field_errors）
        const msg =
          (e.response && e.response.data && (e.response.data.detail || e.response.data.non_field_errors)) ||
          '识别失败，请重试'
        Message.error(Array.isArray(msg) ? msg.join('；') : msg)
      } finally {
        this.previewing = false
      }
    },
    addRow() {
      this.preview.rows.push({ department: '', monitor_person_times: 0 })
    },
    removeRow(idx) {
      this.preview.rows.splice(idx, 1)
    },
    async handleConfirm() {
      // 过滤空科室行
      const rows = this.preview.rows
        .filter(r => (r.department || '').trim())
        .map(r => ({ department: r.department.trim(), monitor_person_times: Number(r.monitor_person_times) || 0 }))
      if (!rows.length) {
        Message.warning('没有可入库的科室数据')
        return
      }
      this.confirming = true
      try {
        const payload = { stat_date: this.statDate || this.todayStr(), rows }
        if (this.previewFileId) payload.file_id = this.previewFileId
        const res = await monitorStatApi.confirmRows(payload)
        Message.success(`入库成功，共保存 ${res.data.saved} 条（${res.data.stat_date}）`)
        this.previewDone = false
        this.imageFile = null
        this.previewFileId = null
        if (this.$refs.imgUpload) this.$refs.imgUpload.clearFiles()
        this.fetchDashboard()
        this.refreshHistory()
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.detail || e.response.data.non_field_errors)) ||
          '入库失败，请重试'
        Message.error(Array.isArray(msg) ? msg.join('；') : msg)
      } finally {
        this.confirming = false
      }
    },
    // ---- 区块2：Excel 上传 ----
    onExcelChange(file) {
      const raw = file.raw || file
      if (!raw) return
      this.excelFile = raw
      this.excelResult = null
    },
    onExcelRemove() {
      this.excelFile = null
      this.excelResult = null
    },
    async handleExcelUpload() {
      if (!this.excelFile) return
      this.uploadingExcel = true
      try {
        const fd = new FormData()
        fd.append('file', this.excelFile)
        // 落库日期由前端指定（后端原来写死 today，补录历史日期会错位）
        fd.append('stat_date', this.excelDate || this.todayStr())
        const res = await monitorStatApi.uploadExcel(fd)
        this.excelResult = { total_devices: res.data.total_devices, dept_count: res.data.dept_count }
        Message.success(`监护仪清单上传成功（落库日期 ${res.data.stat_date || this.excelDate}）`)
        this.fetchDashboard()
        this.refreshHistory()
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.detail || e.response.data.non_field_errors)) ||
          'Excel 解析失败'
        Message.error(Array.isArray(msg) ? msg.join('；') : msg)
      } finally {
        this.uploadingExcel = false
      }
    },
    // 入库/Excel 上传成功后刷新「历史查询」区块（组件未挂载时静默跳过）
    refreshHistory() {
      const h = this.$refs.history
      if (h && typeof h.reload === 'function') h.reload()
    },
    // ---- 区块3：大屏当前统计 ----
    async fetchDashboard() {      this.loadingDash = true
      try {
        const res = await monitorStatApi.getDashboard()
        const d = res.data || {}
        this.dashboard = {
          person_date: d.person_date || null,
          person_total: d.person_total || 0,
          device_total: d.device_total || 0,
          device_total_fixed: !!d.device_total_fixed,
          rows: Array.isArray(d.rows) ? d.rows : [],
        }
      } catch (e) {
        // 后端未启动 / 无权限时静默失败
        this.dashboard = {
          person_date: null, person_total: 0, device_total: 0,
          device_total_fixed: false, rows: [],
        }
      } finally {
        this.loadingDash = false
      }
    },
  },
}
</script>

<style scoped>
/* ===================================================================
   监护统计页：深色大屏风格（2026-10-24 第二次改版）
   配色沿用主页大屏 HomeDashboard：
     底 #0a1628 / 面板 #0f2137 / 描边 #1b3a5c / 主色青 #22d3ee
   el-main 本身已是 #0a1628，所以页面根节点不刷底色，只排卡片。
=================================================================== */
.monitor-stat-page {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* ⚠️ 必须写：Home.vue 的 scoped `.app-container{height:100vh}` 会通过
     「子组件根元素继承父 scoped 属性」命中本页根节点。上一版是普通 block，
     100vh 只导致多滚一点；改成 flex 列之后，卡片会被当成可收缩的 flex item
     压扁，再叠加卡片自带的 overflow:hidden → 表格行被无声裁掉（这个坑踩过）。 */
  height: auto !important;
}

/* ===== 卡片：深蓝底 + 青灰描边 + 微弱外发光 ===== */
.ms-card {
  position: relative;
  flex: 0 0 auto; /* 不参与收缩，高度由内容决定 */
  background: #0f2137;
  border: 1px solid #1b3a5c;
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(34, 211, 238, 0.08);
  overflow: hidden;
}
.ms-card-hd {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #1b3a5c;
  background: linear-gradient(180deg, rgba(34, 211, 238, 0.07) 0%, rgba(15, 33, 55, 0) 100%);
}
.ms-card-title {
  position: relative;
  padding-left: 11px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #e6f6ff;
  white-space: nowrap;
}
.ms-card-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 13px;
  border-radius: 2px;
  background: #22d3ee;
  box-shadow: 0 0 8px rgba(34, 211, 238, 0.7);
}
.ms-card-sub {
  font-size: 12px;
  color: #6b8ca8;
  line-height: 1.5;
}
.ms-card-tools {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}
.ms-card-bd {
  padding: 14px 16px;
}

/* ===== 录入区：左主右辅双栏，窄屏自动堆叠 ===== */
.entry-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.entry-main {
  flex: 2 1 520px;
  min-width: 0;
}
.entry-side {
  flex: 1 1 300px;
  min-width: 0;
}
@media (max-width: 1200px) {
  .entry-row {
    flex-direction: column;
  }
  .entry-main,
  .entry-side {
    width: 100%;
  }
}

/* ===== 当前统计的 KPI 三连 ===== */
.kpi-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}
.kpi {
  flex: 1 1 140px;
  min-width: 140px;
  padding: 10px 14px;
  background: rgba(34, 211, 238, 0.05);
  border: 1px solid #1b3a5c;
  border-radius: 6px;
}
.kpi-val {
  font-size: 22px;
  font-weight: 700;
  color: #22d3ee;
  line-height: 1.1;
  text-shadow: 0 0 10px rgba(34, 211, 238, 0.45);
}
.kpi-unit {
  font-size: 12px;
  color: #6b8ca8;
  margin-left: 3px;
}
.kpi-label {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6b8ca8;
}

.upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.match-alert {
  margin-bottom: 12px;
}
.table-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.edit-table {
  margin-bottom: 4px;
}
.danger-link {
  color: #ff7875 !important;
}
.dash-date {
  font-size: 12px;
  color: #6b8ca8;
}
/* 右辅卡片的补充说明 */
.side-tip {
  margin: 4px 0 0;
  font-size: 12px;
  color: #5b7a95;
  line-height: 1.7;
}
/* 「一期固定值」小标：提醒这个数字不是台账统计出来的 */
.fixed-flag {
  font-style: normal;
  font-size: 11px;
  color: #f6ad55;
  margin-left: 4px;
  padding: 0 4px;
  border: 1px solid rgba(246, 173, 85, 0.45);
  background: rgba(246, 173, 85, 0.12);
  border-radius: 3px;
}
.mini-empty {
  color: #5b7a95;
  font-size: 13px;
  padding: 16px 0;
  text-align: center;
}

/* ===================================================================
   Element UI 深色适配（仅在 .monitor-stat-page 内生效）
   用 /deep/ 穿透子组件（MonitorStatHistory 也在覆盖范围内）。
   弹层类组件（日期面板 / 下拉列表）挂在 body 上，保持浅色不变 ——
   深色页面上浅色浮层反而更清楚，也避免全局污染。
=================================================================== */
/* ---- 表格 ---- */
.monitor-stat-page /deep/ .el-table,
.monitor-stat-page /deep/ .el-table tr,
.monitor-stat-page /deep/ .el-table th.el-table__cell,
.monitor-stat-page /deep/ .el-table td.el-table__cell {
  background-color: transparent;
}
.monitor-stat-page /deep/ .el-table th.el-table__cell {
  background-color: rgba(34, 211, 238, 0.08);
  color: #8fd6e8;
  font-weight: 600;
  border-bottom: 1px solid #1b3a5c;
}
.monitor-stat-page /deep/ .el-table td.el-table__cell {
  color: #cfe6f5;
  border-bottom: 1px solid rgba(27, 58, 92, 0.75);
}
.monitor-stat-page /deep/ .el-table--border,
.monitor-stat-page /deep/ .el-table--border th.el-table__cell,
.monitor-stat-page /deep/ .el-table--border td.el-table__cell {
  border-color: #1b3a5c;
}
.monitor-stat-page /deep/ .el-table::before,
.monitor-stat-page /deep/ .el-table--border::after,
.monitor-stat-page /deep/ .el-table--group::after {
  background-color: #1b3a5c;
}
.monitor-stat-page /deep/ .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background-color: rgba(34, 211, 238, 0.09);
}
.monitor-stat-page /deep/ .el-table__empty-block {
  background: transparent;
}
.monitor-stat-page /deep/ .el-table__empty-text {
  color: #5b7a95;
}

/* ---- 输入框 / 数字框 / 日期区间 ---- */
.monitor-stat-page /deep/ .el-input__inner {
  background-color: rgba(10, 22, 40, 0.65);
  border-color: #1b3a5c;
  color: #cfe6f5;
}
.monitor-stat-page /deep/ .el-input__inner::placeholder {
  color: #4f6b83;
}
.monitor-stat-page /deep/ .el-input__inner:hover {
  border-color: #2b5c86;
}
.monitor-stat-page /deep/ .el-input__icon,
.monitor-stat-page /deep/ .el-input__prefix,
.monitor-stat-page /deep/ .el-input__suffix {
  color: #6b8ca8;
}
.monitor-stat-page /deep/ .el-input-number__decrease,
.monitor-stat-page /deep/ .el-input-number__increase {
  background: rgba(34, 211, 238, 0.08);
  border-color: #1b3a5c;
  color: #8fd6e8;
}
.monitor-stat-page /deep/ .el-input-number__decrease:hover,
.monitor-stat-page /deep/ .el-input-number__increase:hover {
  color: #22d3ee;
}
.monitor-stat-page /deep/ .el-range-input {
  background: transparent;
  color: #cfe6f5;
}
.monitor-stat-page /deep/ .el-range-separator {
  color: #6b8ca8;
}

/* ---- 按钮 ---- */
.monitor-stat-page /deep/ .el-button {
  background: rgba(34, 211, 238, 0.08);
  border-color: #1b3a5c;
  color: #a8d8e8;
}
.monitor-stat-page /deep/ .el-button:hover {
  background: rgba(34, 211, 238, 0.18);
  border-color: #22d3ee;
  color: #e6f6ff;
}
.monitor-stat-page /deep/ .el-button--primary {
  background: #0e7490;
  border-color: #22d3ee;
  color: #eafcff;
}
.monitor-stat-page /deep/ .el-button--primary:hover {
  background: #0891b2;
}
.monitor-stat-page /deep/ .el-button--success {
  background: #0f766e;
  border-color: #2dd4bf;
  color: #eafcff;
}
.monitor-stat-page /deep/ .el-button--success:hover {
  background: #0d9488;
}
.monitor-stat-page /deep/ .el-button--text {
  background: transparent;
  border-color: transparent;
}
.monitor-stat-page /deep/ .el-button.is-disabled,
.monitor-stat-page /deep/ .el-button.is-disabled:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: #1b3a5c;
  color: #4b6478;
}

/* ---- 提示条 ---- */
.monitor-stat-page /deep/ .el-alert--success {
  background: rgba(45, 212, 191, 0.1);
  border: 1px solid rgba(45, 212, 191, 0.35);
}
.monitor-stat-page /deep/ .el-alert--warning {
  background: rgba(246, 173, 85, 0.1);
  border: 1px solid rgba(246, 173, 85, 0.35);
}
.monitor-stat-page /deep/ .el-alert__title {
  color: #cfe6f5;
}
.monitor-stat-page /deep/ .el-alert__icon {
  color: #2dd4bf;
}
.monitor-stat-page /deep/ .el-alert--warning .el-alert__icon {
  color: #f6ad55;
}

/* ---- 页签 ---- */
.monitor-stat-page /deep/ .el-tabs__item {
  color: #6b8ca8;
}
.monitor-stat-page /deep/ .el-tabs__item:hover {
  color: #8fd6e8;
}
.monitor-stat-page /deep/ .el-tabs__item.is-active {
  color: #22d3ee;
}
.monitor-stat-page /deep/ .el-tabs__active-bar {
  background-color: #22d3ee;
}
.monitor-stat-page /deep/ .el-tabs__nav-wrap::after {
  background-color: #1b3a5c;
}

/* ---- 单选按钮组 ---- */
.monitor-stat-page /deep/ .el-radio-button__inner {
  background: rgba(34, 211, 238, 0.06);
  border-color: #1b3a5c;
  color: #8fd6e8;
}
.monitor-stat-page /deep/ .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #0e7490;
  border-color: #22d3ee;
  color: #eafcff;
  box-shadow: -1px 0 0 0 #22d3ee;
}

/* ---- 分页 ---- */
.monitor-stat-page /deep/ .el-pagination.is-background .btn-prev,
.monitor-stat-page /deep/ .el-pagination.is-background .btn-next,
.monitor-stat-page /deep/ .el-pagination.is-background .el-pager li {
  background: rgba(34, 211, 238, 0.07);
  color: #a8d8e8;
}
.monitor-stat-page /deep/ .el-pagination.is-background .el-pager li.active {
  background: #0e7490;
  color: #eafcff;
}
.monitor-stat-page /deep/ .el-pagination.is-background .el-pager li:not(.disabled):hover {
  color: #22d3ee;
}
.monitor-stat-page /deep/ .el-pagination__total,
.monitor-stat-page /deep/ .el-pagination__jump {
  color: #6b8ca8;
}

/* ---- 上传列表 ---- */
.monitor-stat-page /deep/ .el-upload-list__item {
  color: #a8d8e8;
}
.monitor-stat-page /deep/ .el-upload-list__item:hover {
  background-color: rgba(34, 211, 238, 0.08);
}
.monitor-stat-page /deep/ .el-upload-list__item .el-icon-close {
  color: #8fd6e8;
}
</style>
